import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Globe, MapPin } from "lucide-react";
import { LEGAL_META } from "./content";
import { openCookiePreferences } from "../components/CookieConsent";

function Wordmark() {
  return (
    <Link to="/" className="inline-flex items-baseline gap-2" data-testid="legal-wordmark">
      <span className="font-display text-[19px] font-semibold tracking-tight text-ink">
        Sajilo<span className="text-blue">Health</span>
      </span>
      <span className="font-deva text-[11px] text-textsoft hidden sm:inline">सजिलो ल्याब्स</span>
    </Link>
  );
}

function renderBlock(block, idx) {
  switch (block.type) {
    case "h3":
      return (
        <h3 key={idx} className="mt-8 font-display text-[16px] font-semibold text-ink tracking-tight">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p key={idx} className="mt-3 text-[15px] text-textsoft leading-[1.7]">
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul key={idx} className="mt-3 space-y-2 text-[15px] text-textsoft leading-[1.7] pl-1">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-[10px] w-1.5 h-1.5 rounded-full bg-green shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <div
          key={idx}
          className="mt-5 rounded-xl border border-green/20 bg-green-soft px-5 py-4 text-[14px] text-green-dark leading-[1.65]"
        >
          {block.text}
        </div>
      );
    default:
      return null;
  }
}

export default function LegalPage({ doc, testId }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = `${doc.title} — SajiloHealth`;
  }, [doc.title]);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid={testId}>
      {/* Top bar */}
      <header className="border-b border-line bg-paper/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[960px] mx-auto px-5 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Wordmark />
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-textsoft hover:text-ink transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.8} />
            Back to home
          </Link>
        </div>
      </header>

      {/* Title */}
      <section className="pt-16 sm:pt-20 pb-10 border-b border-line">
        <div className="max-w-[960px] mx-auto px-5 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2">
            <span className="w-6 h-px bg-line-strong" />
            <span className="kicker text-textsoft">Legal</span>
          </div>
          <h1
            data-testid="legal-title"
            className="mt-5 font-display text-[36px] sm:text-[44px] lg:text-[52px] font-semibold tracking-tight leading-[1.05]"
          >
            {doc.title}
          </h1>
          <p className="mt-4 text-[16px] text-textsoft max-w-2xl leading-[1.6]">{doc.intro}</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
            <div className="rounded-xl border border-line bg-white px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-textsoft">Effective</div>
              <div className="mt-1 text-[13.5px] font-semibold text-ink">{LEGAL_META.effective}</div>
            </div>
            <div className="rounded-xl border border-line bg-white px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-textsoft">Last updated</div>
              <div className="mt-1 text-[13.5px] font-semibold text-ink">{LEGAL_META.updated}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <main className="py-14 sm:py-16">
        <div className="max-w-[960px] mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* TOC */}
            <aside className="lg:col-span-4 hidden lg:block">
              <div className="sticky top-24">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-textsoft">Contents</div>
                <ol className="mt-4 space-y-2.5">
                  {doc.sections.map((s) => (
                    <li key={s.n} className="flex gap-3">
                      <span className="font-mono text-[11px] text-textsoft/70 mt-[3px]">{s.n}</span>
                      <a
                        href={`#sec-${s.n}`}
                        data-testid={`legal-toc-${s.n}`}
                        className="text-[13px] text-textsoft hover:text-ink leading-snug"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            {/* Sections */}
            <article className="lg:col-span-8">
              {doc.sections.map((section) => (
                <section
                  key={section.n}
                  id={`sec-${section.n}`}
                  data-testid={`legal-section-${section.n}`}
                  className="scroll-mt-24 mb-14 last:mb-0"
                >
                  <div className="inline-flex items-center gap-3">
                    <span className="font-mono text-[11px] font-semibold tracking-[0.18em] text-green">
                      {section.n}
                    </span>
                    <span className="h-px w-8 bg-line-strong" />
                  </div>
                  <h2 className="mt-3 font-display text-[22px] sm:text-[26px] font-semibold text-ink tracking-tight">
                    {section.title}
                  </h2>
                  <div className="mt-4">{section.blocks.map((b, i) => renderBlock(b, i))}</div>
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>

      {/* Contact card */}
      <section className="border-t border-line py-14 bg-white">
        <div className="max-w-[960px] mx-auto px-5 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-paper border border-line p-7 sm:p-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-textsoft">Questions?</div>
            <h3 className="mt-3 font-display text-[22px] font-semibold text-ink tracking-tight">
              We are happy to help.
            </h3>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <a
                href={`mailto:${LEGAL_META.email}`}
                data-testid="legal-contact-email"
                className="flex items-start gap-3 rounded-xl border border-line bg-white px-4 py-3 hover:border-line-strong transition-colors"
              >
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-green-soft text-green">
                  <Mail size={16} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-textsoft">Email</div>
                  <div className="text-[13.5px] font-semibold text-ink">{LEGAL_META.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-3 rounded-xl border border-line bg-white px-4 py-3">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-paper-sunken text-ink">
                  <Globe size={16} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-textsoft">Website</div>
                  <div className="text-[13.5px] font-semibold text-ink">sajilohealth.com</div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-line bg-white px-4 py-3">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-paper-sunken text-ink">
                  <MapPin size={16} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-textsoft">Location</div>
                  <div className="text-[13.5px] font-semibold text-ink">{LEGAL_META.location}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="py-10 border-t border-line bg-paper">
        <div className="max-w-[960px] mx-auto px-5 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-[12px] text-textsoft">
            © {new Date().getFullYear()} Sajilo Labs Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-[12px] font-medium text-textsoft hover:text-ink transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-[12px] font-medium text-textsoft hover:text-ink transition-colors">
              Terms
            </Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="text-[12px] font-medium text-textsoft hover:text-ink transition-colors"
            >
              Cookies
            </button>
            <a
              href={`mailto:${LEGAL_META.email}`}
              className="text-[12px] font-medium text-textsoft hover:text-ink transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
